import type { PropertyValues } from 'lit'
import type { FieldContextValue } from '../field/field-context.js'
import type {
  InputGroupControlState,
  InputGroupPresentationContextValue,
  InputGroupSize,
} from './_InputGroupContext.js'
import { ContextConsumer } from '@lit/context'
import { LitElement, noChange } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { StateAttributesController } from '../common/controllers/StateAttributesController.js'
import { injectLightDomStyles, setLightDomScope } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { fieldContext } from '../field/field-context.js'
import { inputGroupPresentationContext, requestInputGroupContext } from './_InputGroupContext.js'
import { InputGroupContextProvider } from './controllers/InputGroupContextProvider.js'
import style from './InputGroup.light-dom.css'

type SupportedControl = HTMLElement & {
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  label?: string
  hint?: string
  error?: string
  focus: (options?: FocusOptions) => void
  [requestInputGroupContext]?: () => void
}

type OwnedPart = 'shell' | 'label' | 'hint' | 'error'

const scopeKey = 'nord-input-group'
const supportedControlSelector = ':scope > nord-input:not([slot]), :scope > nord-textarea:not([slot])'
const fieldPartSlots = new Set(['label', 'hint', 'error'])
const interactiveSelector = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[tabindex]:not([tabindex="-1"])',
  'nord-button',
  'nord-dropdown',
].join(',')

let helperId = 0

/**
 * Input Group composes exactly one Nord Input or Textarea with reusable add-ons
 * while leaving the editable control, form state, and all consumer nodes owned
 * by the consumer.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot - A single `nord-input` or `nord-textarea` plus Input Group Addons.
 * @slot label - Optional rich label content for a standalone group.
 * @slot hint - Optional rich hint content for a standalone group.
 * @slot error - Optional rich validation message for a standalone group.
 *
 * @attr {boolean} data-invalid - Present while the effective Field/group state is invalid.
 * @attr {boolean} data-disabled - Present while the effective Field/group state is disabled.
 * @attr {boolean} data-readonly - Present while the group or control is readonly.
 * @attr {boolean} data-focused - Present while the control or an add-on action has focus.
 * @attr {boolean} data-focus-visible - Present while the text control has keyboard-visible focus.
 * @attr {boolean} data-composition-invalid - Present unless exactly one supported text control is composed.
 *
 * @cssprop [--n-input-group-inline-size=100%] - Controls the group's inline size.
 * @cssprop [--n-input-group-background=var(--n-color-surface)] - Controls the shared control surface.
 * @cssprop [--n-input-group-background-inactive=var(--n-color-active)] - Controls the disabled and readonly surface.
 * @cssprop [--n-input-group-border-color=var(--n-color-border-strong)] - Controls the shared border colour.
 * @cssprop [--n-input-group-border-radius=var(--n-border-radius-s)] - Controls the shared corner radius.
 * @cssprop [--n-input-group-target-size] - Overrides the size-derived compound control minimum block size.
 */
@customElement('nord-input-group')
export default class InputGroup extends DraftComponentMixin(LitElement) {
  /** Controls the effective size of the grouped Input or Textarea and add-ons. */
  @property({ reflect: true }) size: 's' | 'm' | 'l' = 'm'

  /** Plain-text label used when the group is not inside a Nord Field. */
  @property({ reflect: true }) label = ''

  /** Optional standalone hint text. */
  @property({ reflect: true }) hint?: string

  /** Places the standalone hint below the group surface. */
  @property({ reflect: true, type: Boolean, attribute: 'hint-below' }) hintBelow = false

  /** Optional standalone error text. Supplying it makes the group invalid. */
  @property({ reflect: true }) error?: string

  /** Makes the grouped text control required. */
  @property({ reflect: true, type: Boolean }) required = false

  /** Visually hides the standalone label while preserving its accessible name. */
  @property({ reflect: true, type: Boolean, attribute: 'hide-label' }) hideLabel = false

  /** Hides the visual required indicator without changing native required state. */
  @property({ reflect: true, type: Boolean, attribute: 'hide-required' }) hideRequired = false

  /** Applies invalid state when the group is standalone. */
  @property({ reflect: true, type: Boolean }) invalid = false

  /** Disables the grouped text control without implicitly disabling add-on actions. */
  @property({ reflect: true, type: Boolean }) disabled = false

  /** Makes the grouped text control readonly. */
  @property({ reflect: true, type: Boolean }) readonly = false

  private outerField = new ContextConsumer(this, {
    context: fieldContext,
    subscribe: true,
    callback: () => this.scheduleSynchronization(),
  })

  private fieldProvider = new InputGroupContextProvider(
    this,
    fieldContext,
    undefined,
    target => this.acceptsContextTarget(target),
  )

  private presentationProvider = new InputGroupContextProvider(
    this,
    inputGroupPresentationContext,
    undefined,
    target => this.acceptsContextTarget(target),
  )

  private presentationOwner = {}
  private observer?: MutationObserver
  private synchronizationQueued = false
  private compositionValid = false
  private supportedControl?: SupportedControl
  private registeredControl?: HTMLElement
  private controlState?: InputGroupControlState
  private controlRequired = false
  private lastDiagnostic?: string
  private owned = new Map<OwnedPart, HTMLElement>()

  private stateAttributes = new StateAttributesController(
    this,
    () => ({
      'invalid': this.effectiveInvalid,
      'disabled': this.effectiveDisabled,
      'readonly': this.effectiveReadonly,
      'focus-visible': this.controlState?.focusVisible ?? false,
      'composition-invalid': !this.compositionValid,
      'inside-field': this.outerField.value !== undefined,
      'has-error': !this.outerField.value && Boolean(this.effectiveError),
    }),
    { trackFocus: true },
  )

  connectedCallback() {
    super.connectedCallback()
    injectLightDomStyles(this, style, 'nord-input-group')
    this.addEventListener('click', this.handleClick)
    this.observer ??= new MutationObserver(records => this.handleMutations(records))
    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['disabled', 'error', 'hint', 'hint-below', 'invalid', 'label', 'position', 'readonly', 'required', 'size', 'slot'],
      childList: true,
      characterData: true,
      subtree: true,
    })
    this.scheduleSynchronization()
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    this.observer?.disconnect()
    this.observer = undefined
    this.outerField.value?.setRequired(false)
    this.outerField.value = undefined
    this.registeredControl = undefined
    this.controlState = undefined
    this.controlRequired = false
    this.requestUpdate()
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected render() {
    return noChange
  }

  protected firstUpdated() {
    this.synchronize()
  }

  protected updated(changed: PropertyValues<this>) {
    if (changed.size > 0) {
      this.scheduleSynchronization()
    }
  }

  private get effectiveInvalid() {
    if (this.outerField.value) {
      return this.outerField.value.invalid
    }
    return this.invalid || Boolean(this.effectiveError) || Boolean(this.controlState?.invalid)
  }

  private get effectiveSize(): InputGroupSize {
    return this.size === 's' || this.size === 'l' ? this.size : 'm'
  }

  private get effectiveDisabled() {
    return Boolean(this.outerField.value?.disabled) || this.disabled || Boolean(this.controlState?.disabled)
  }

  private get effectiveReadonly() {
    return this.readonly || Boolean(this.controlState?.readonly)
  }

  private get effectiveRequired() {
    return this.required || this.controlRequired || Boolean(this.controlState?.required)
  }

  private get effectiveLabel() {
    return this.directSlot('label')?.textContent?.trim()
      || this.label.trim()
      || this.supportedControl?.label?.trim()
      || ''
  }

  private get effectiveHint() {
    return this.directSlot('hint')?.textContent?.trim()
      || this.hint?.trim()
      || this.supportedControl?.hint?.trim()
      || ''
  }

  private get effectiveError() {
    return this.directSlot('error')?.textContent?.trim()
      || this.error?.trim()
      || this.supportedControl?.error?.trim()
      || ''
  }

  private handleMutations(records: MutationRecord[]) {
    const onlyOwned = records.every(record => this.isOwnedNode(record.target))
    if (!onlyOwned) {
      this.scheduleSynchronization()
    }
  }

  private isOwnedNode(node: Node) {
    const element = node instanceof Element ? node : node.parentElement
    return Boolean(element?.closest('[data-n-input-group-owned]'))
  }

  private scheduleSynchronization() {
    if (this.synchronizationQueued) {
      return
    }
    this.synchronizationQueued = true
    queueMicrotask(() => {
      this.synchronizationQueued = false
      if (this.isConnected) {
        this.synchronize()
      }
    })
  }

  private synchronize() {
    const controls = Array.from(this.querySelectorAll<SupportedControl>(supportedControlSelector))
    const unsupportedChildren = Array.from(this.children)
      .filter(element => !this.isSupportedDirectChild(element))
    const previousValid = this.compositionValid
    const previousControl = this.supportedControl
    this.compositionValid = controls.length === 1 && unsupportedChildren.length === 0
    this.supportedControl = this.compositionValid ? controls[0] : undefined

    if (previousControl !== this.supportedControl) {
      this.registeredControl = undefined
      this.controlState = undefined
      this.controlRequired = false
    }

    this.ensureShell()
    this.syncOwnedFieldParts()

    const presentation = this.compositionValid ? this.createPresentationContext() : undefined
    const field = this.compositionValid ? this.createFieldContext() : undefined
    this.presentationProvider.setValue(presentation)
    this.fieldProvider.setValue(field)
    if (previousControl !== this.supportedControl) {
      this.supportedControl?.[requestInputGroupContext]?.()
    }
    this.publishRequired()

    if (this.compositionValid) {
      this.lastDiagnostic = undefined
    }
    else {
      this.diagnose(
        'nord-input-group requires exactly one direct nord-input or nord-textarea '
        + 'and only nord-input-group-addon or named label, hint, and error content; '
        + `received ${controls.length} supported control(s) and ${unsupportedChildren.length} unsupported direct child(ren).`,
      )
    }

    if (previousValid !== this.compositionValid || previousControl !== this.supportedControl) {
      this.requestUpdate()
    }
  }

  private isSupportedDirectChild(element: Element) {
    if (this.isOwnedNode(element)) {
      return true
    }
    const slot = element.getAttribute('slot') ?? ''
    return fieldPartSlots.has(slot)
      || (!slot && element.matches('nord-input, nord-textarea, nord-input-group-addon'))
  }

  private createPresentationContext(): InputGroupPresentationContextValue {
    const directAddons = Array.from(
      this.querySelectorAll<HTMLElement & { position?: string }>(':scope > nord-input-group-addon'),
    )
    return {
      owner: this.presentationOwner,
      size: this.effectiveSize,
      required: this.required,
      readonly: this.readonly,
      hasInlineStartAddon: directAddons.some(addon => addon.position === 'inline-start'),
      hasInlineEndAddon: directAddons.some(addon => addon.position === 'inline-end'),
      registerControl: this.registerControl,
      updateControl: this.updateControlState,
      unregisterControl: this.unregisterControl,
    }
  }

  private createFieldContext(): FieldContextValue {
    const outer = this.outerField.value
    if (outer) {
      return {
        ...outer,
        disabled: this.effectiveDisabled,
        setRequired: this.setControlRequired,
      }
    }

    const label = this.directSlot('label') ?? this.owned.get('label')
    const describedByParts = this.hintBelow
      ? [
          this.directSlot('error') ?? this.owned.get('error'),
          this.directSlot('hint') ?? this.owned.get('hint'),
        ]
      : [
          this.directSlot('hint') ?? this.owned.get('hint'),
          this.directSlot('error') ?? this.owned.get('error'),
        ]
    const describedByElements = describedByParts.filter(
      (element): element is HTMLElement => Boolean(element),
    )
    const describedBy = describedByElements
      .map(element => this.ensureId(element))
      .join(' ')

    return {
      invalid: this.effectiveInvalid,
      disabled: this.effectiveDisabled,
      labelId: label ? this.ensureId(label) : undefined,
      labelText: this.effectiveLabel || undefined,
      describedById: describedBy || undefined,
      describedByElements,
      setRequired: this.setControlRequired,
    }
  }

  private acceptsContextTarget(target: Element) {
    return this.compositionValid
      && target.parentElement === this
      && target === this.supportedControl
  }

  private registerControl = (control: HTMLElement, state: InputGroupControlState) => {
    if (control !== this.supportedControl) {
      return
    }
    this.registeredControl = control
    this.setControlState(state)
  }

  private unregisterControl = (control: HTMLElement) => {
    if (control !== this.registeredControl) {
      return
    }
    this.registeredControl = undefined
    this.controlState = undefined
    this.requestUpdate()
  }

  private updateControlState = (control: HTMLElement, state: InputGroupControlState) => {
    if (control !== this.supportedControl) {
      return
    }
    this.registeredControl = control
    this.setControlState(state)
  }

  private setControlState(state: InputGroupControlState) {
    if (this.controlState && this.sameControlState(this.controlState, state)) {
      return
    }
    this.controlState = state
    this.controlRequired = state.required
    this.requestUpdate()
    this.scheduleSynchronization()
  }

  private sameControlState(left: InputGroupControlState, right: InputGroupControlState) {
    return left.disabled === right.disabled
      && left.readonly === right.readonly
      && left.required === right.required
      && left.invalid === right.invalid
      && left.focusVisible === right.focusVisible
  }

  private setControlRequired = (required: boolean) => {
    if (this.controlRequired === required) {
      return
    }
    this.controlRequired = required
    this.publishRequired()
    this.scheduleSynchronization()
  }

  private publishRequired() {
    this.outerField.value?.setRequired(this.effectiveRequired)
  }

  private ensureShell() {
    let shell = this.owned.get('shell')
    if (!shell) {
      shell = document.createElement('div')
      this.owned.set('shell', shell)
    }
    shell.className = 'n-input-group-shell'
    shell.dataset.nInputGroupOwned = 'shell'
    shell.setAttribute('aria-hidden', 'true')
    setLightDomScope(scopeKey, shell)
    if (shell.parentElement !== this) {
      this.prepend(shell)
    }
  }

  private syncOwnedFieldParts() {
    if (this.outerField.value || !this.compositionValid) {
      this.removeOwnedPart('label')
      this.removeOwnedPart('hint')
      this.removeOwnedPart('error')
      return
    }

    this.syncOwnedPart('label', this.effectiveLabel)
    this.syncOwnedPart('hint', this.effectiveHint)
    this.syncOwnedPart('error', this.effectiveError)

    const prefix = [this.owned.get('label'), ...(this.hintBelow ? [] : [this.owned.get('hint')]), this.owned.get('shell')]
      .filter((element): element is HTMLElement => Boolean(element))
    const currentPrefix = Array.from(this.children).slice(0, prefix.length)
    if (!prefix.every((element, index) => currentPrefix[index] === element)) {
      this.prepend(...prefix)
    }
    const error = this.owned.get('error')
    const suffix = [error, ...(this.hintBelow ? [this.owned.get('hint')] : [])]
      .filter((element): element is HTMLElement => Boolean(element))
    const currentSuffix = Array.from(this.children).slice(-suffix.length)
    if (!suffix.every((element, index) => currentSuffix[index] === element)) {
      this.append(...suffix)
    }
  }

  private syncOwnedPart(part: Exclude<OwnedPart, 'shell'>, text: string) {
    if (this.directSlot(part) || !text) {
      this.removeOwnedPart(part)
      return
    }

    let element = this.owned.get(part)
    if (!element) {
      element = document.createElement(part === 'label' ? 'label' : 'div')
      element.className = `n-input-group-${part}`
      element.dataset.nInputGroupOwned = part
      if (part === 'error') {
        element.setAttribute('role', 'alert')
        element.setAttribute('aria-live', 'polite')
      }
      setLightDomScope(scopeKey, element)
      this.owned.set(part, element)
    }

    if (part === 'label') {
      const required = this.effectiveRequired && !this.hideRequired
      const signature = `${text}\u0000${required}`
      if (element.dataset.nInputGroupContent !== signature) {
        const labelText = document.createTextNode(text)
        const indicator = document.createElement('span')
        indicator.className = 'n-input-group-required'
        indicator.setAttribute('aria-hidden', 'true')
        indicator.textContent = '*'
        setLightDomScope(scopeKey, indicator)
        element.replaceChildren(labelText, ...(required ? [indicator] : []))
        element.dataset.nInputGroupContent = signature
      }
      element.toggleAttribute('data-n-input-group-visually-hidden', this.hideLabel)
    }
    else if (element.textContent !== text) {
      element.textContent = text
    }
  }

  private removeOwnedPart(part: Exclude<OwnedPart, 'shell'>) {
    this.owned.get(part)?.remove()
    this.owned.delete(part)
  }

  private directSlot(name: 'label' | 'hint' | 'error') {
    return Array.from(this.children).find(
      (element): element is HTMLElement => element instanceof HTMLElement && element.slot === name,
    )
  }

  private ensureId(element: HTMLElement) {
    if (!element.id) {
      element.id = `n-input-group-helper-${helperId++}`
    }
    return element.id
  }

  private handleClick = (event: MouseEvent) => {
    const path = event.composedPath()
    const focusRegion = path.find((target) => {
      if (!(target instanceof HTMLElement) || target.parentElement !== this) {
        return false
      }
      return target.localName === 'nord-input-group-addon'
        || target.slot === 'label'
        || target.dataset.nInputGroupOwned === 'label'
    })
    if (!focusRegion) {
      return
    }

    const regionIndex = path.indexOf(focusRegion)
    const interactive = path.slice(0, regionIndex).some(
      target => target instanceof Element && target.matches(interactiveSelector),
    )
    if (!interactive) {
      this.supportedControl?.focus({ preventScroll: true })
    }
  }

  private diagnose(message: string) {
    if (this.lastDiagnostic === message) {
      return
    }
    this.lastDiagnostic = message
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`NORD: ${message}`)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-input-group': InputGroup
  }
}
